import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { AvatarDisplay, AvatarDisplayType } from '@/components/Avatar/AvatarDisplay/AvatarDisplay';
import { BottomNavbarStickyItem } from '@/components/Revamp/Navbar/BottomNavbarSticky/BottomNavbarStickyItem';
import { HomeIcon } from '@/components/Revamp/Navbar/HomeIcon';
import { LeaderboardIcon } from '@/components/Revamp/Navbar/LeaderboardIcon';
import { NotebookIcon } from '@/components/Revamp/Navbar/NotebookIcon';
import { useAuthContext } from '@/contexts/authContext';
import { useAvatarSelection } from '@/hooks/avatar/useAvatarSelection';
import { authenticatedRoutes } from '@/router/routes';
import { toClassNames } from '@/utils';

type Props = {
  className?: string;
};

/**
 * Duplicate a lot of code from BottomNavbar for now. After we turn on the feature flag, we will clean up the BottomNavbar component.
 */
export const BottomNavbarSticky: FC<Props> = ({ className = '' }) => {
  const { t } = useTranslation('common');
  const { user } = useAuthContext();
  const { data: avatarSelection } = useAvatarSelection();

  const currentAvatarUrl = avatarSelection?.avatarItem?.imageUrl;
  const currentDecorationUrl = avatarSelection?.headAccessoryItem?.equippedImageUrl;
  const currentBackgroundUrl = avatarSelection?.backgroundItem?.imageUrl;

  const { classroomId } = useParams();
  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    if (classroomId) {
      params.set('classroomId', classroomId);
    }
    return params;
  }, [classroomId]);

  const userName = useMemo(() => {
    if (user?.firstname && user?.lastname) {
      return `${user.firstname} ${user.lastname}`;
    } else if (user?.firstname) {
      return user.firstname;
    }
    return t('nav_bar.profile');
  }, [user, t]);

  return (
    <div className={toClassNames(['flex gap-1 border-t border-gray-100 bg-white px-3', className])}>
      <BottomNavbarStickyItem to={authenticatedRoutes.Classrooms.getRoute()} label={t('nav_bar.home')}>
        <HomeIcon />
      </BottomNavbarStickyItem>
      <BottomNavbarStickyItem
        to={authenticatedRoutes.LeaderboardPodium.getRoute({ query: queryParams.toString() })}
        label={t('nav_bar.leaderboard')}
      >
        <LeaderboardIcon />
      </BottomNavbarStickyItem>
      <BottomNavbarStickyItem to={authenticatedRoutes.UserNotebook.getRoute()} label={t('nav_bar.notebook')}>
        <div className="flex items-center justify-center">
          <NotebookIcon />
        </div>
      </BottomNavbarStickyItem>
      <BottomNavbarStickyItem to={authenticatedRoutes.Profile.getRoute()} label={userName}>
        <div className="h-8 w-8 overflow-hidden rounded-full">
          <AvatarDisplay
            variant={AvatarDisplayType.Default}
            avatarImageUrl={currentAvatarUrl}
            decorationImageUrl={currentDecorationUrl}
            backgroundImageUrl={currentBackgroundUrl}
          />
        </div>
      </BottomNavbarStickyItem>
    </div>
  );
};
